import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ExportColumn<T> {
  header: string;
  value: (item: T) => string | number | null | undefined;
}

export interface PDFExportOptions {
  title?: string;
  subtitle?: string;
  filename?: string;
  pageSize?: 'A4' | 'Letter';
  orientation?: 'portrait' | 'landscape';
}

// ============================================================================
// CSV EXPORT
// ============================================================================

export function exportToCSV<T>(
  data: T[],
  columns: ExportColumn<T>[],
  filename: string = 'export'
): void {
  const escape = (value: string | number | null | undefined) => {
    const str = String(value ?? '');
    const safe = /^[=+\-@]/.test(str) ? `'${str}` : str; // Prevent formula injection
    return `"${safe.replace(/"/g, '""')}"`;
            };

  const headers = columns.map(col => escape(col.header));
  const rows = data.map(item =>
    columns.map(col => escape(col.value(item))).join(',')
  );

  const csv = '\uFEFF' + [headers.join(','), ...rows].join('\r\n'); // BOM for Excel
  
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

// ============================================================================
// PDF EXPORT
// ============================================================================

export function exportToPDF<T>(
  data: T[],
  columns: ExportColumn<T>[],
  options: PDFExportOptions = {}
): void {
  const {
    title = 'Export',
    subtitle,
    filename = 'export',
    pageSize = 'A4',
    orientation = 'landscape',
  } = options;

  const doc = new jsPDF({
    orientation,
    unit: 'mm',
    format: pageSize.toLowerCase() as any,
  });

  // Title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 15);

  // Subtitle
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(subtitle, 14, 22);
    doc.setTextColor(0);
  }

  // Table Data
  const headers = columns.map(col => col.header);
  const rows = data.map(item =>
    columns.map(col => {
      const value = col.value(item);
      return value === null || value === undefined ? '—' : String(value);
    })
  );

  // Generate Table
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: subtitle ? 28 : 22,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [37, 99, 235], // Tailwind blue-600
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251], // Tailwind gray-50
    },
    margin: { top: subtitle ? 28 : 22, left: 14, right: 14 },
    didDrawPage: (data) => {
      const pageCount = (doc as any).internal.getNumberOfPages();
      const pageSize = (doc as any).internal.pageSize;
      const pageHeight = pageSize.height || pageSize.getHeight();
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150);
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        (pageSize.width || pageSize.getWidth()) / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    },
  });

  doc.save(`${filename}.pdf`);
}

// ============================================================================
// HELPER: Convert DataTable columns to export columns
// ============================================================================

export function prepareExportColumns<T>(
  columns: Array<{
    key: string;
    header: string;
    accessor?: keyof T;
    type?: string;
    exportValue?: (item: T) => string | number;
  }>
): ExportColumn<T>[] {
  return columns
    .filter(col => col.type !== 'actions')
    .map(col => ({
      header: col.header,
      value: (item: T) => {
        if (col.exportValue) return col.exportValue(item);
        const value = item[col.accessor ?? col.key as keyof T];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        return value;
      },
    }));
}
